import { hasAnswerObservation } from '@/services/checklist/checklistObservations';
import { confirmsChecklistMediaPredecessor, readChecklistMediaSuccession } from './ChecklistMediaSuccessionContract';

type Row = Record<string, any>;

export const pendingMediaMatchesValue = (pending: Row, value: Row): boolean =>
  pending.item_type === 'CHECKLIST' && pending.item_id === value.checklist_id &&
  pending.empresa_id === value.empresa_id && Boolean(pending.user_id) &&
  pending.campo_id === (value.snapshot_field_id || value.campo_id) &&
  Boolean(value.valor) && (value.valor === pending.local_uri || value.valor === pending.prepared_public_url);

/** Conserva a versão remota da imagem quando somente sua nota é editada. */
export const checklistObservationMediaVersion = (incoming: Row, existing: Row | undefined): string | undefined =>
  incoming.valor === undefined && Object.prototype.hasOwnProperty.call(incoming, 'observacao') &&
  typeof existing?.valor === 'string' && /^https?:\/\//.test(existing.valor) &&
  typeof existing.updated_at === 'string' && Number.isFinite(Date.parse(existing.updated_at))
    ? existing.updated_at : undefined;

/** Protege a intenção de mídia e a nota pendente, inclusive antes da captura. */
export const remoteWouldReplacePendingMedia = (value: Row, remote: Row, images: Row[], operations: Row[]): boolean => {
  const linked = images.filter(pending => pendingMediaMatchesValue(pending, value));
  const changesValue = Object.prototype.hasOwnProperty.call(remote, 'valor') && remote.valor !== value.valor;
  const changesNote = Object.prototype.hasOwnProperty.call(remote, 'observacao') &&
    (remote.observacao ?? null) !== (value.observacao ?? null);
  if (linked.length && changesValue) return true;
  if (!changesValue && !changesNote) return false;
  return operations.some(operation => {
    if (operation.operation !== 'checklist_partial_update' || operation.collection !== 'guincho_valores_checklist' ||
        operation.item_id !== value.checklist_id || operation.empresa_id !== value.empresa_id || !operation.user_id ||
        (linked.length && !linked.some(p => p.user_id === operation.user_id && p.empresa_id === operation.empresa_id))) return false;
    let answers;
    try { answers = typeof operation.data === 'string' ? JSON.parse(operation.data) : operation.data; } catch { return linked.length > 0; }
    // Os dois IDs pertencem à mesma linha validada; uma fila anterior ao enriquecimento usa o físico.
    return [...new Set([value.snapshot_field_id, value.campo_id].filter(Boolean))].some(key => {
      const answer = answers?.[key];
      if (changesValue && answers && Object.prototype.hasOwnProperty.call(answers, key)) {
        const queuedValue = answer && typeof answer === 'object' ? answer.valor : answer;
        if (queuedValue !== undefined && queuedValue === value.valor) return true;
      }
      if (!hasAnswerObservation(answer) || answer.observacao !== (value.observacao ?? null)) return false;
      if (linked.length) return changesNote;
      return (!Object.prototype.hasOwnProperty.call(answer, 'valor') || answer.valor === value.valor) &&
        typeof value.valor === 'string' && /^https?:\/\//.test(value.valor) &&
        typeof value.updated_at === 'string' && Number.isFinite(Date.parse(value.updated_at));
    });
  });
};

export interface ChecklistMediaObservationBaselineInput {
  pending: Row;
  value: Row;
  previousRemoteValue: unknown;
  previousRemoteUpdatedAt: string | null;
  remoteUpdatedAt: string;
  canAcknowledge: () => boolean;
  succession?: { archive: Row; evidence: Row };
}

/** O ACK da própria nota autoriza somente a evolução de seu baseline anterior. */
export const canAdvanceMediaObservationBaseline = (input: ChecklistMediaObservationBaselineInput): boolean => {
  const { pending, value } = input;
  if (input.succession) {
    const link = readChecklistMediaSuccession(input.succession.archive, pending);
    return Boolean(link && confirmsChecklistMediaPredecessor(link, input.succession.evidence) &&
      pendingMediaMatchesValue(pending, value) && value.id === link.value.id &&
      (value.campo_id ?? null) === (link.value.campo_id ?? null) &&
      (value.snapshot_field_id ?? null) === (link.value.snapshot_field_id ?? null) &&
      typeof input.remoteUpdatedAt === 'string' && Number.isFinite(Date.parse(input.remoteUpdatedAt)) &&
      input.previousRemoteValue === link.predecessor.prepared_public_url && pending.baseline_captured === 1 &&
      ((pending.expected_remote_value ?? null) === (link.successor.expected_remote_value ?? null) &&
        (pending.expected_remote_updated_at ?? null) === (link.successor.expected_remote_updated_at ?? null) ||
        pending.expected_remote_value === input.previousRemoteValue));
  }
  return pendingMediaMatchesValue(pending, value) &&
    typeof input.remoteUpdatedAt === 'string' && Number.isFinite(Date.parse(input.remoteUpdatedAt)) &&
    (pending.baseline_captured !== 1 ||
      (pending.expected_remote_value ?? null) === (input.previousRemoteValue ?? null) &&
      ((pending.expected_remote_updated_at ?? null) === input.previousRemoteUpdatedAt ||
        pending.expected_remote_updated_at === input.remoteUpdatedAt));
};
